import { ChangeEvent, FunctionComponent, useMemo } from "react";
import { UseFormSetValue } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";
import { InputField } from "@/components/InputField/InputField";

import { SelectField } from "@/components/SelectField/SelectField";

import { CreateCompanyEnrich } from "../schema/companyEnrich";
import {
	FUNDING_MAX_OPTIONS,
	FUNDING_MIN_OPTIONS,
	FUNDING_ROUNDS,
} from "../constants/fundingRounds.constants";

interface FundingFiltersSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
}

const CURRENT_YEAR = new Date().getFullYear();

const FUNDING_YEAR_OPTIONS = [
	{ label: "Any", value: null },
	...Array.from({ length: CURRENT_YEAR - 1990 }, (_, i) => {
		const year = 1991 + i - 1;

		return { label: String(year), value: year };
	}),
];

export const FundingFiltersSection: FunctionComponent<FundingFiltersSectionProps> = ({
	formValues,
	setValue,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	return (
		<>
			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					stayOn
					data={FUNDING_ROUNDS}
					handleSelect={(value) => {
						setValue(
							"companyFilterSearch.fundingRounds",
							value?.map((r) => r.id.toString()) || [],
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					label={ts("fundingRoundsLabel")}
					placeholder={ts("fundingRoundsPlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.fundingRounds?.map((r) => ({
							id: r,
							title: FUNDING_ROUNDS.find((e) => e.id === r)?.title || r,
						})) || []
					}
				/>
			</div>

			<div className="flex flex-row items-end gap-4 w-full mb-4">
				<SelectField
					label={ts("fundingAmountLabel")}
					name="fundingAmountMin"
					selectedOption={formValues.companyFilterSearch?.fundingAmount?.min ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.fundingAmount",
							{ ...formValues.companyFilterSearch?.fundingAmount, min: val as number | null },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={FUNDING_MIN_OPTIONS.map(({ label, value }) => ({
						title: label,
						value,
					}))}
					hideEmptyValue
				/>

				<span className="-translate-y-1/2 text-[14px]">{ts("to")}</span>

				<SelectField
					name="fundingAmountMax"
					selectedOption={formValues.companyFilterSearch?.fundingAmount?.max ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.fundingAmount",
							{ ...formValues.companyFilterSearch?.fundingAmount, max: val as number | null },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={FUNDING_MAX_OPTIONS.map(({ label, value }) => ({
						title: label,
						value,
					}))}
					hideEmptyValue
				/>
			</div>

			<div className="flex flex-row items-end gap-4 w-full mb-4">
				<SelectField
					label={ts("fundingYearLabel")}
					name="fundingYearMin"
					selectedOption={formValues.companyFilterSearch?.fundingYear?.min ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.fundingYear",
							{ ...formValues.companyFilterSearch?.fundingYear, min: val as number | null },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={FUNDING_YEAR_OPTIONS.map(({ label, value }) => ({
						title: label,
						value,
					}))}
					hideEmptyValue
				/>

				<span className="-translate-y-1/2 text-[14px]">{ts("to")}</span>

				<SelectField
					name="fundingYearMax"
					selectedOption={formValues.companyFilterSearch?.fundingYear?.max ?? null}
					handleSelect={(val) => {
						setValue(
							"companyFilterSearch.fundingYear",
							{ ...formValues.companyFilterSearch?.fundingYear, max: val as number | null },
							{ shouldDirty: true, shouldValidate: true }
						);
					}}
					options={FUNDING_YEAR_OPTIONS.map(({ label, value }) => ({
						title: label,
						value,
					}))}
					hideEmptyValue
				/>
			</div>
		</>
	);
};
